// Defense-in-depth sanitization for CMS-authored rich text before it is
// injected via dangerouslySetInnerHTML (RawHtml) or html-react-parser
// (parseToHtml). Cosmic content is authored internally, so this guards against
// a compromised/mistaken editor or bad CMS data, not public user input.
//
// Implemented as a PURE string -> string transform (no DOM / jsdom) so it runs
// identically on the server and the client and produces byte-identical output.
// That parity is essential: these strings are server-rendered and hydrated, so
// a server/client divergence would cause a React hydration mismatch.
//
// It is a BLOCKLIST of dangerous constructs, not a tag allowlist, so the rich
// markup the site relies on (iframes, imgix images, tables, Tailwind classes,
// data-* attributes, the h1p->p output, etc.) is preserved.

const DANGEROUS_URL_SCHEME = /^(?:javascript|vbscript|data):/i;

// Control characters and spaces (U+0000–U+0020) that browsers strip from a URL;
// removing them closes obfuscation such as "java\tscript:". The control-char
// range is intentional here.
// eslint-disable-next-line no-control-regex
const URL_IGNORED_CHARS = /[\u0000-\u0020]+/g;

// Decode the HTML entities a browser would resolve inside an attribute value,
// so an obfuscated scheme such as `&#106;avascript:` or `java&Tab;script:` is
// caught. Browsers do not recursively decode, so a single pass is sufficient.
const MAX_CODE_POINT = 0x10ffff;

// String.fromCodePoint throws RangeError for a code point above U+10FFFF, so an
// out-of-range numeric entity in CMS content (e.g. &#9999999999;) would crash
// rendering. Leave any out-of-range reference untouched — it isn't a decoded
// scheme character anyway.
const fromCodePoint = (match: string, codePoint: number): string =>
  codePoint <= MAX_CODE_POINT ? String.fromCodePoint(codePoint) : match;

const decodeEntities = (value: string): string =>
  value
    .replace(/&#x([0-9a-f]+);?/gi, (m, hex) =>
      fromCodePoint(m, parseInt(hex, 16))
    )
    .replace(/&#(\d+);?/g, (m, dec) => fromCodePoint(m, parseInt(dec, 10)))
    .replace(/&colon;/gi, ':')
    .replace(/&tab;/gi, '\t')
    .replace(/&newline;/gi, '\n');

// A URL is dangerous if, after entity-decoding and removing the control/space
// characters browsers ignore inside URLs, it begins with a scriptable scheme.
const hasDangerousScheme = (rawValue: string): boolean => {
  const normalized = decodeEntities(rawValue)
    .replace(URL_IGNORED_CHARS, '')
    // Drop leading quotes so a malformed/unclosed value like `"javascript:...`
    // (captured together with its opening quote) is still scheme-checked.
    .replace(/^["']+/, '')
    .trim();

  return DANGEROUS_URL_SCHEME.test(normalized);
};

// An attribute can start not only after whitespace but immediately after a
// preceding attribute's quoted value (`src="x"onerror=…`) or a "/"
// (`<img src=1 /onerror=…>`) — the HTML tokenizer does not require a space.
// So the boundary before the attribute name is [\s/"'] (or start of string),
// captured and re-emitted. This is stricter than \s-only, which is bypassable.
//
// Limitation: a pure regex can't fully track the tokenizer, so a value that
// literally contains ` onx=…` could be over-stripped. Accepted trade-off for
// internally-authored (semi-trusted) CMS content.
// Attributes removed outright: inline event handlers (on*), and iframe srcdoc
// (a nested, same-origin document that a string-level pass can't see into once
// entity-encoded — iframes themselves are intentionally preserved).
const STRIPPED_ATTR =
  /(^|[\s/"'])(?:on[a-z]+|srcdoc)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;

// Attributes whose URL value is scheme-checked. Covers every attribute that can
// carry a scriptable URL: href/src/xlink:href plus <object data>, poster, and
// form action/formaction. data-src / srcset stay out of scope (boundary before
// them is "-"/"t", not [\s/"']).
const URL_ATTR =
  /(^|[\s/"'])((?:xlink:)?(?:href|src|data|poster|action|formaction))\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;

// Both passes run to a fixpoint: a global replace skips a boundary char that
// was consumed by the previous match, so back-to-back handlers/attrs
// (`"x"onA=".."onB=".."`) need another pass. Each pass removes/rewrites at
// least one, so this converges.
const stripDangerousAttributes = (html: string): string => {
  let out = html;
  let previous: string;
  do {
    previous = out;
    out = out.replace(STRIPPED_ATTR, '$1');
  } while (out !== previous);

  return out;
};

const neutralizeDangerousUrls = (html: string): string => {
  let out = html;
  let previous: string;
  do {
    previous = out;
    out = out.replace(
      URL_ATTR,
      (match, boundary, attrName, doubleQuoted, singleQuoted, bare) => {
        const value = doubleQuoted ?? singleQuoted ?? bare ?? '';

        return hasDangerousScheme(value) ? `${boundary}${attrName}="#"` : match;
      }
    );
  } while (out !== previous);

  return out;
};

export const sanitizeCosmicHtml = (html: string): string => {
  // Preserve the existing tolerance for empty / undefined input.
  if (!html) return html;

  // 1. Remove <script> / <style> blocks entirely (matched pairs first, then
  //    any orphan opening tag). innerHTML never executes injected <script>,
  //    but stripping them is cheap defense-in-depth.
  const withoutScripts = html
    .replace(/<\s*(script|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(?:script|style)\b[^>]*\/?>/gi, '');

  // 2. Strip dangerous attributes (on* handlers, iframe srcdoc), then
  //    3. neutralize scriptable URL schemes in the URL-bearing attributes.
  return neutralizeDangerousUrls(stripDangerousAttributes(withoutScripts));
};
